/**
 * The Responsible Party control: an internal underwriter or user, picked from a
 * list — or Unassigned. It used to be a free-text box, so the same person was
 * "M. Chen", "Mike Chen" and "mchen@…" and a grouped view split them three ways.
 *
 * The list is the catalogue's ordered `assignee` roster, served by
 * `GET /loans/field-definitions`. A deal that already holds an owner
 * the list never heard of — older deals do — keeps it as a visible, marked option
 * rather than having it silently cleared by the control.
 */
import { useQuery } from '@tanstack/react-query'
import type { CSSProperties } from 'react'

import { dealFileService } from '@/services/dealFile'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'

export const NOT_ON_THE_LIST = ' (not on the list)'

export function ResponsiblePartySelect({
  value,
  onChange,
  className,
  ariaLabel = 'Responsible Party',
  disabled = false,
  style,
}: {
  value: string
  onChange: (next: string) => void
  className?: string
  ariaLabel?: string
  disabled?: boolean
  style?: CSSProperties
}) {
  const catalogue = useQuery({
    queryKey: LOAN_QUERY_KEYS.dealFieldCatalogue,
    queryFn: () => dealFileService.getCatalogue(),
    staleTime: 5 * 60 * 1000,
  })
  const options = catalogue.data?.fields.find((f) => f.name === 'assignee')?.options ?? []

  const legacy = value && !options.includes(value) ? value : null
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      disabled={disabled || options.length === 0}
      title={catalogue.isError ? 'Responsible Party list could not be loaded' : undefined}
      onChange={(e) => onChange(e.target.value)}
      className={className}
      style={style}
    >
      {!value ? <option value="" disabled>—</option> : null}
      {options.map((name) => (
        <option key={name} value={name}>{name}</option>
      ))}
      {legacy ? <option value={legacy}>{legacy}{options.length ? NOT_ON_THE_LIST : ''}</option> : null}
    </select>
  )
}
